// Kassenschicht: öffnen mit gezähltem Wechselgeld, abschließen mit Zählung nach Stückelung, Differenz mit Grund,
// Freigabe über der Grenze der Kasse durch eine zweite Person – und der Abschlussbericht. Nach dem Abschluss nimmt
// die Schicht nichts mehr an. Alles im Schulungs- und Demomodus (lib/labels.ts: POS_BANNER).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { DENOMINATIONS } from '../lib/labels'
import { zTillEcho, zTillProof } from '../lib/offline/types'
import { zAmountCentsOrZero, zDay, zLocationRef, zOptionalText, zPerson, zRegisterRef, zShiftState, zTaxRow } from './common'

const READ = 'pos.shift.read'

const zDenomination = z.int().refine((value) => (DENOMINATIONS as readonly number[]).includes(value), { error: 'Diese Stückelung gibt es nicht.' })

export const zCountEntry = z.strictObject({
  denominationCents: zDenomination,
  count: z.int({ error: 'Bitte eine Anzahl eingeben.' }).min(0, { error: 'Die Anzahl darf nicht negativ sein.' }).max(100_000, { error: 'Die Anzahl ist zu groß.' }),
})

export const zShiftTotals = z.object({
  receiptCount: z.int(),
  returnCount: z.int(),
  /** Umsatz brutto: Bons abzüglich Rücknahmen, mit Pfandsaldo, ohne verkaufte Wertgutscheine */
  turnoverCents: z.int(),
  /** Warenwert brutto nach Rabatten, ohne Pfand */
  goodsCents: z.int(),
  /** gewährte Rabatte (positiv) */
  discountCents: z.int(),
  depositChargedCents: z.int(),
  /** erstattetes Pfand: Leergut und Pfand zurückgenommener Ware (positiv) */
  depositRefundedCents: z.int(),
  voucherSoldCents: z.int(),
  voucherRedeemedCents: z.int(),
  /** Wert der Rücknahmen (positiv) */
  returnsCents: z.int(),
  taxes: z.array(zTaxRow),
  payments: z.array(z.object({ method: z.string(), label: z.string(), amountCents: z.int(), count: z.int() })),
  voids: z.object({ lineCount: z.int(), lineCents: z.int(), receiptCount: z.int(), receiptCents: z.int(), ageRefusedCount: z.int(), discountGrantCount: z.int() }),
  cash: z.object({
    floatCents: z.int(),
    /** bar eingenommen */
    salesCents: z.int(),
    /** bar erstattet oder ausgezahlt über Bons (positiv) */
    refundsCents: z.int(),
    /** Einlagen */
    movementsInCents: z.int(),
    /** Entnahmen, Geldtransit, Auszahlungen (positiv) */
    movementsOutCents: z.int(),
    /** Soll-Bestand – bei offener Schicht nur für Personen mit Freigaberecht sichtbar (Zählung ohne Vorgabe) */
    expectedCents: z.int().nullable(),
  }),
})
export type ShiftTotals = z.output<typeof zShiftTotals>

export const zShiftHead = z.object({
  id: zId,
  register: zRegisterRef,
  businessDate: z.string(),
  /** Nummer des Abschlusses, fortlaufend je Kasse */
  zNo: z.int().nullable(),
  state: zShiftState,
  stateLabel: z.string(),
  openedAt: z.date(),
  openedBy: zPerson,
  closedAt: z.date().nullable(),
  closedBy: zPerson.nullable(),
  approvalRequired: z.boolean(),
  approvedAt: z.date().nullable(),
  approvedBy: zPerson.nullable(),
  approvalNote: z.string().nullable(),
  floatCents: z.int(),
  countedCents: z.int().nullable(),
  expectedCents: z.int().nullable(),
  diffCents: z.int().nullable(),
})

export const zShiftRow = zShiftHead.extend({
  turnoverCents: z.int(),
  receiptCount: z.int(),
})

export const shiftOpen = defineContract({
  name: 'pos.shift.open',
  title: 'Kassenschicht öffnen',
  description: 'Öffnet die Schicht einer Kasse mit dem gezählten Wechselgeld.',
  llm: { description: 'Öffnet die Schicht einer Kasse mit dem gezählten Wechselgeld in Cent. Nur an der Kasse selbst – das Geld wird dabei gezählt.', keywords: ['Schicht öffnen', 'Kasse öffnen'] },
  input: z.strictObject({ registerId: zId, floatCents: zAmountCentsOrZero, note: zOptionalText(200), /** Pflicht an Offline-Kassen, mit nextBonId */ till: zTillProof.optional() }),
  output: z.object({ id: zId, businessDate: z.string(), message: z.string(), till: zTillEcho.optional() }),
  permission: 'pos.shift.open',
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const shiftClose = defineContract({
  name: 'pos.shift.close',
  title: 'Kasse zählen und Schicht abschließen',
  description: 'Schließt die Schicht mit der Zählung nach Stückelung ab; eine Differenz braucht einen Grund, über der Grenze der Kasse eine Freigabe.',
  llm: {
    description: 'Schließt die Schicht einer Kasse mit der Zählung des Bargelds nach Stückelung ab. Nur an der Kasse selbst – das Geld wird dabei gezählt.',
    keywords: ['Kassenabschluss', 'Kasse zählen', 'Schicht abschließen'],
  },
  input: z.strictObject({
    shiftId: zId,
    counts: z.array(zCountEntry).max(DENOMINATIONS.length * 2),
    /** Pflicht, sobald gezählter und erwarteter Bestand voneinander abweichen */
    differenceReason: zOptionalText(300),
    /** was beim Abschluss aus der Kasse zur Bank bzw. in den Tresor geht */
    transferCents: zAmountCentsOrZero.default(0),
    /** Pflicht an Offline-Kassen, mit nextBonId; der Abschluss prüft zusätzlich §11.3 (leere Warteschlange, Klärfälle) */
    till: zTillProof.optional(),
  }),
  output: z.object({
    id: zId,
    zNo: z.int(),
    state: zShiftState,
    expectedCents: z.int(),
    countedCents: z.int(),
    diffCents: z.int(),
    approvalRequired: z.boolean(),
    message: z.string(),
    till: zTillEcho.optional(),
  }),
  permission: 'pos.shift.count',
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
})

export const shiftApprove = defineContract({
  name: 'pos.shift.approve',
  title: 'Kassendifferenz freigeben',
  description: 'Gibt den Abschluss einer Schicht frei, deren Differenz über der Grenze der Kasse liegt.',
  llm: {
    description:
      'Gibt den Abschluss einer bereits gezählten und abgeschlossenen Schicht frei, deren Differenz über der Grenze ' +
      'der Kasse liegt (Zustand „Freigabe offen“ in pos.shift.list). Die Freigabe ändert keine Zahl, sie hält fest, ' +
      'wer die Differenz gesehen hat. Nicht möglich für Schichten, die die Person selbst geöffnet, gezählt oder ' +
      'abgeschlossen hat. Nicht für offene Schichten.',
    keywords: ['Kassendifferenz freigeben', 'Abschluss freigeben', 'Kassenabschluss'],
  },
  input: z.strictObject({ shiftId: zId, note: zOptionalText(300) }),
  output: z.object({ id: zId, state: zShiftState, message: z.string() }),
  permission: 'pos.shift.approve',
  risk: 'financial',
  scope: 'record',
})

export const shiftList = defineContract({
  name: 'pos.shift.list',
  title: 'Kassenschichten anzeigen',
  description: 'Schichten der Kassen mit Zustand, Umsatz und Kassendifferenz.',
  llm: {
    description:
      'Listet Kassenschichten (jüngste zuerst): Kasse, Standort, Geschäftstag, Zustand (open = offen, closed = ' +
      'abgeschlossen, pending_approval = Freigabe offen, approved = freigegeben), Umsatz brutto in Cent, Anzahl Bons, ' +
      'gezählter und erwarteter Bestand sowie Differenz in Cent. Filter: Standort, Kasse, Zustand, Zeitraum. ' +
      'Verwenden für „welche Abschlüsse warten auf Freigabe“ oder „Umsatz der Kasse Garching diese Woche“. Den ' +
      'vollständigen Bericht einer Schicht liefert pos.shift.get. Daten aus dem Schulungs- und Demomodus der Kasse.',
    keywords: ['Kassenschicht', 'Schichten', 'Kassenabschluss', 'Kassendifferenz', 'Tagesabschluss', 'Umsatz Kasse'],
  },
  input: listInput({
    sortBy: ['businessDate'],
    defaultSort: { by: 'businessDate', dir: 'desc' },
    filters: {
      locationId: zId.optional(),
      registerId: zId.optional(),
      state: zShiftState.optional(),
      businessDateFrom: zDay.optional(),
      businessDateTo: zDay.optional(),
    },
  }),
  output: listOutput(zShiftRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const zClosing = z.object({
  zNo: z.int(),
  createdAt: z.date(),
  firstBonId: z.int().nullable(),
  lastBonId: z.int().nullable(),
  vorgangCount: z.int(),
  businessCases: z.array(z.object({ gvTyp: z.string(), gvName: z.string().nullable(), vatKey: z.int(), grossCents: z.int(), netCents: z.int(), taxCents: z.int() })),
  payments: z.array(z.object({ type: z.string(), name: z.string(), amountCents: z.int() })),
  totalPaymentsCents: z.int(),
  /** Bargeldbestand nach Differenz und Geldtransit */
  cashBalanceCents: z.int(),
})

export const zShiftEvent = z.object({
  kind: z.string(),
  label: z.string(),
  description: z.string(),
  amountCents: z.int(),
  reason: z.string().nullable(),
  operatorName: z.string().nullable(),
  authorizedByName: z.string().nullable(),
  at: z.date(),
})

export const zShiftMovement = z.object({
  id: zId,
  number: z.string().nullable(),
  kind: z.string(),
  label: z.string(),
  amountCents: z.int(),
  reason: z.string(),
  byName: z.string().nullable(),
  at: z.date(),
})

export const shiftGet = defineContract({
  name: 'pos.shift.get',
  title: 'Abschlussbericht einer Schicht',
  description: 'Bericht einer Schicht: Umsatz nach Steuersatz und Zahlart, Rabatte, Stornos, Rücknahmen, Gutscheine, Bargeld, Zählung.',
  llm: {
    description:
      'Liefert den Bericht EINER Kassenschicht: Umsatz brutto und je Steuersatz (netto, Steuer, brutto in Cent), je ' +
      'Zahlart, Rabatte, Stornos vor der Zahlung, Rücknahmen, verkaufte und ' +
      'eingelöste Gutscheine, Bargeldbewegungen und – nach dem Abschluss – Zählung, Soll, Ist, Differenz mit Grund ' +
      'und Freigabe. Bei offener Schicht ist es der Zwischenstand. Schichten findet pos.shift.list. Der Bericht ist ' +
      'als DEMO gekennzeichnet und kein Tagesabschluss für die Buchführung.',
    keywords: ['Abschlussbericht', 'Tagesabschluss', 'Kassenbericht', 'Zwischenbericht', 'Kassendifferenz'],
  },
  input: z.strictObject({ shiftId: zId }),
  output: z.object({
    shift: zShiftHead,
    totals: zShiftTotals,
    count: z
      .object({
        denominations: z.array(z.object({ denominationCents: z.int(), count: z.int(), amountCents: z.int() })),
        countedCents: z.int(),
        diffCents: z.int(),
        transferCents: z.int(),
        differenceReason: z.string().nullable(),
        countedBy: zPerson.nullable(),
      })
      .nullable(),
    events: z.array(zShiftEvent),
    movements: z.array(zShiftMovement),
    /** noch offene (auch geparkte) Bons – sie verhindern den Abschluss */
    openBasketCount: z.int(),
    /** Grenze der Kasse, ab der eine Differenz freigegeben werden muss */
    diffLimitCents: z.int(),
    /** was die Person mit dieser Schicht tun darf */
    allowed: z.object({ close: z.boolean(), approve: z.boolean(), moveCash: z.boolean() }),
    seller: z.object({ name: z.string(), lines: z.array(z.string()) }),
    /** Kennzeichnung außerhalb des Echtbetriebs („DEMO“, „TEST“) – null im Echtbetrieb */
    mark: z.string().nullable(),
    notice: z.string().nullable(),
    fiscalNotice: z.string().nullable(),
    /** Kassenabschluss: Z-Nummer, Vorgangsnummern, Summen je Geschäftsvorfall und Zahlart (nur Vorgangstyp „Beleg“) */
    closing: zClosing.nullable(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
})

export const shiftOverview = defineContract({
  name: 'pos.shift.overview',
  title: 'Kassen heute',
  description: 'Offene Schichten und der Umsatz des Tages je Markt.',
  llm: {
    description:
      'Liefert je Markt den Kassenumsatz eines Geschäftstags (Standard: heute) brutto in Cent, die Anzahl der Bons, ' +
      'die offenen Schichten (Kasse, seit wann, wer) und wie viele Abschlüsse auf Freigabe warten. Verwenden für ' +
      '„wie läuft die Kasse heute“ oder „Umsatz Garching gestern“. Einstandswerte und Roherträge enthält die Antwort ' +
      'nicht. Daten aus dem Schulungs- und Demomodus der Kasse.',
    keywords: ['Kassenumsatz', 'Umsatz heute', 'offene Schichten', 'Tagesumsatz', 'Kasse heute'],
  },
  input: z.strictObject({ businessDate: zDay.optional() }),
  output: z.object({
    businessDate: z.string(),
    totalCents: z.int(),
    receiptCount: z.int(),
    pendingApprovalCount: z.int(),
    stores: z.array(
      z.object({
        location: zLocationRef,
        turnoverCents: z.int(),
        receiptCount: z.int(),
        openShifts: z.array(z.object({ id: zId, registerId: zId, registerName: z.string(), openedAt: z.date(), openedByName: z.string() })),
        pendingApprovalCount: z.int(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

/** Zähler für die Navigation: Abschlüsse, die auf die Freigabe dieser Person warten */
export const shiftTodoCount = defineContract({
  name: 'pos.shift.todoCount',
  title: 'Offene Freigaben der Kasse',
  description: 'Anzahl der Kassenabschlüsse, die auf Freigabe warten.',
  llm: { description: 'Zählt die Kassenabschlüsse, die auf die Freigabe durch diese Person warten. Für die Navigation; Einzelheiten liefert pos.shift.list mit state pending_approval.', keywords: ['Freigaben Kasse'] },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})
